import { Product } from '../types';

/**
 * Generates high-performance responsive image attributes for product images.
 * Specifically optimizes Unsplash image URLs dynamically by appending width, crop, quality, and format parameters.
 * Falls back gracefully to standard attributes for base64 (data:), local assets, or other CDNs.
 * 
 * @param imageUrl The URL of the product image
 * @param defaultWidth The base fallback width of the image (in pixels)
 * @returns An object containing src, srcSet, sizes, loading, and decoding attributes
 */
export function getProductImageProps(
  imageUrl: string | undefined,
  defaultWidth: number = 300,
  isPriority: boolean = false
) {
  if (!imageUrl || typeof imageUrl !== 'string') {
    return {
      src: '',
      loading: 'lazy' as const,
      decoding: 'async' as const,
    };
  }

  const cleanUrl = imageUrl.trim();
  if (!cleanUrl) {
    return {
      src: '',
      loading: 'lazy' as const,
      decoding: 'async' as const,
    };
  }

  const loading: 'lazy' | 'eager' = isPriority ? 'eager' : 'lazy';
  const decoding: 'async' | 'sync' | 'auto' = 'async';

  // Base64/Data URL - no srcset or resize needed
  if (cleanUrl.startsWith('data:')) {
    return {
      src: cleanUrl,
      loading,
      decoding,
    };
  }

  // Unsplash CDN Optimization
  if (cleanUrl.includes('images.unsplash.com')) {
    try {
      // Split the URL at '?' to handle existing search parameters safely
      const baseUrl = cleanUrl.split('?')[0];

      const getUnsplashUrl = (width: number, quality = 75) => {
        return `${baseUrl}?auto=format&fit=crop&w=${width}&q=${quality}`;
      };

      // Define responsive widths tailored to grid/mobile card dimensions
      const widths = [120, 240, 360, 480, 720, 960];
      const srcSet = widths.map(w => `${getUnsplashUrl(w, 75)} ${w}w`).join(', ');

      return {
        src: getUnsplashUrl(defaultWidth, 80),
        srcSet,
        sizes: "(max-width: 480px) 140px, (max-width: 768px) 220px, (max-width: 1024px) 280px, 320px",
        loading,
        decoding,
      };
    } catch (e) {
      console.warn('[ImageOptimizer] Failed to process Unsplash URL:', e);
    }
  }

  // Standard fallback for any other HTTP/HTTPS image URL
  return {
    src: cleanUrl,
    loading,
    decoding,
  };
}
